import { useCallback, useEffect, useRef } from 'react';
import { useStore } from '../store';
import { useUser } from '@clerk/clerk-react';
import { SupabaseClient } from '@supabase/supabase-js';

export const useSubscription = (supabaseClient: SupabaseClient | null) => {
  const { user } = useUser();
  const { 
    isProUser, setIsProUser, 
    isUltraUser, setIsUltraUser,
    setIsEligibleForFreeTrial,
    setSubscriptionEndsAt, setTrialEndsAt, setCancelAtPeriodEnd
  } = useStore();
  
  const isProRef = useRef(isProUser);
  const isUltraRef = useRef(isUltraUser);
  const isSyncing = useStore(state => state.isSyncing);

  useEffect(() => { isProRef.current = isProUser; }, [isProUser]);
  useEffect(() => { isUltraRef.current = isUltraUser; }, [isUltraUser]);

  useEffect(() => {
    if (user?.publicMetadata) {
      // Prevent reverting status while a global sync/reconciliation is in progress
      if (isSyncing) {
        console.log("[useSubscription] Global sync active. Skipping metadata reconciliation.");
        return;
      }

      const { 
        isPro, isUltra, isEligibleForFreeTrial,
        subscriptionEndsAt, trialEndsAt, cancelAtPeriodEnd 
      } = user.publicMetadata as { 
        isPro?: boolean, 
        isUltra?: boolean,
        isEligibleForFreeTrial?: boolean,
        subscriptionEndsAt?: string | null,
        trialEndsAt?: string | null,
        cancelAtPeriodEnd?: boolean
      };
      
      const proVal = !!isPro;
      const ultraVal = !!isUltra;

      // Clerk metadata is only a stand-in until the server's answer arrives this
      // session (see applyEntitlement); after that it may lag and must not write.
      if (useStore.getState().entitlementFromServer) return;

      // Update transient fields in store
      if (isEligibleForFreeTrial !== undefined) setIsEligibleForFreeTrial(!!isEligibleForFreeTrial);
      // Only write dates if the value is truthy — a null from metadata must not overwrite a valid date loaded from Supabase.
      if (subscriptionEndsAt) setSubscriptionEndsAt(subscriptionEndsAt);
      if (trialEndsAt) setTrialEndsAt(trialEndsAt);
      if (cancelAtPeriodEnd !== undefined) setCancelAtPeriodEnd(!!cancelAtPeriodEnd);

      const isHunting = sessionStorage.getItem('isHuntingSubscription') === 'true';
      const hasProChanged = proVal !== isProRef.current;
      const hasUltraChanged = ultraVal !== isUltraRef.current;

      if (hasProChanged || hasUltraChanged) {
        if (isHunting && !proVal && isProRef.current) {
          return;
        }

        // UI only: the plan columns are written by the billing webhook and
        // /api/billing/reconcile with the service role; patch_profile ignores them from the browser.
        setIsProUser(proVal);
        setIsUltraUser(ultraVal);
      }
    }
  }, [user, setIsProUser, setIsUltraUser, setIsEligibleForFreeTrial]);

  return {
    isProUser,
    isUltraUser
  };
};
